import {View, StyleSheet, Text, Pressable, ActivityIndicator} from 'react-native';
import {useRef, useEffect, createRef, useCallback} from 'react';
import {Svg, Path} from 'react-native-svg';
import {useNavigate} from 'extensions/navigation';
import {useSelector} from 'react-redux';
import {theme} from 'config/themes';
import {colors, vectors} from 'config/styles';
import {useDragDropTarget} from 'interface/hooks/useDragDropTarget';
import {useMenuFiles} from 'interface/hooks/useMenuFiles';
import {usePopup} from 'extensions/viewport/usePopup';
import {DropFrame} from 'interface/base/DropFrame';
import {Mouse} from 'interface/base/Mouse';

import type {FilesItem} from 'store/slices/files/types';
import type {ItemInstance} from '@headless-tree/core';
import type {MouseEvent} from 'react';
import type {GestureResponderEvent} from 'react-native';
import * as files from 'store/slices/files';

interface FolderTreeItemProps {
  item: ItemInstance<FilesItem>
  updateDomFocus: () => void;
  isPremium?: boolean;
}

export function FolderTreeItem({item, updateDomFocus, isPremium = false}: FolderTreeItemProps) {
  const expandRef = useRef<NodeJS.Timeout | null>(null);
  const itemRef = useRef(null);
  const navigate = useNavigate();
  const fileItem = useSelector(files.selectors.getItem(item.getId()));
  const views = useSelector(files.selectors.getViews);
  const hasChildren = item.isFolder() && item.getChildren().length > 0;
  const isLoading = item.isFolder() && item.isExpanded() && !views?.[item.getId()];
  const popup = usePopup();
  const {isDropping} = useDragDropTarget({
    ref: itemRef,
    targetId: item.getId(),
    targetName: item.getItemName(),
  });

  // Menu setup
  const menuAnchorRef = createRef<any>();
  const menuFiles = useMenuFiles(
    [item.getId()],
    fileItem,
    {
      primaryColor: colors.neutral.mid,
      hasPrivate: false,
      isPremium,
    }
  );

  const handleMenu = useCallback((e: MouseEvent | GestureResponderEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (popup) {
      if (e?.nativeEvent)
        popup.setPosition({x: e.nativeEvent.pageX, y: e.nativeEvent.pageY});
      popup.setRef(menuAnchorRef);
    }
    menuFiles.open();
  }, [menuFiles, popup]);

  // Auto-expand when dragging over folders
  useEffect(() => {
    if (!item.isFolder()) return;
    if (isDropping && !item.isExpanded()) {
      expandRef.current = setTimeout(() => {
        item.expand();
        updateDomFocus();
      }, 800);
    } else if (!isDropping && expandRef.current) {
      clearTimeout(expandRef.current);
      expandRef.current = null;
    }
    return () => {
      if (expandRef.current) {
        clearTimeout(expandRef.current);
        expandRef.current = null;
      }
    };
  }, [isDropping, item, updateDomFocus]);

  const handleItemClick = () => {
    if (item.isFolder() && !item.isExpanded()) {
      item.expand();
      updateDomFocus();
    }
    navigate(`/folder/${item.getId()}`);
  };

  const handleExpandClick = (e: GestureResponderEvent) => {
    if (!hasChildren || !item.isFolder()) return;
    e.stopPropagation();
    if (item.isExpanded()) {
      item.collapse();
    } else {
      item.expand();
      updateDomFocus();
    }
  };

  return (
    <Pressable
      {...item.getProps()}
      ref={itemRef}
      style={[
        styles.item,
        {paddingLeft: (item.getItemMeta().level + 1) * 16},
      ]}
      onPress={handleItemClick}>
      <Mouse onContextMenu={handleMenu}>
        <View style={styles.container} ref={menuAnchorRef}>
          <Pressable 
            style={styles.thumbnail}
            disabled={!hasChildren}
            onPress={handleExpandClick}>
            {isLoading ? (
              <View style={styles.loading}>
                <ActivityIndicator size="small" color={colors.neutral.mid} />
              </View>
            ) : (
              <Svg
                viewBox="0 0 24 24"
                width={24}
                height={24}
                style={[
                  styles.folderIcon,
                  item.isExpanded() && styles.folderIconExpanded,
                  !hasChildren && {opacity: 0},
                ]}>
                <Path d={vectors.icons.treeExpand} fill={colors.neutral.mid} fillOpacity={0.5} />
              </Svg>
            )}
          </Pressable>
          <Svg
            viewBox="0 0 35 30"
            style={{transform: [{translateY: 5}]}}
            height={20}>
            <Path
              d={vectors.icons.folder}
              fill={colors.neutral.mid}
              fillOpacity={0.5}/>
          </Svg>
          <View style={styles.inner}>
            <Text
              style={[
                styles.title,
                item.isFocused() && styles.focused,
                item.isSelected() && styles.selected,
              ]}
              numberOfLines={1}
              ellipsizeMode="tail">
              {item.getItemName()}
            </Text>
          </View>
        </View>
      </Mouse>
      {isDropping && <DropFrame stroke="small" />}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  item: {
    paddingRight: 8,
    paddingVertical: 4,
    position: 'relative',
  },
  container: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  thumbnail: {
    width: 24,
    flexGrow: 0,
    flexShrink: 0,
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
  },
  folderIcon: {
    marginRight: 8,
  },
  folderIconExpanded: {
    transform: [{rotate: '90deg'}],
  },
  inner: {
    flexGrow: 1,
    flexShrink: 1,
    overflow: 'hidden',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  title: {
    fontSize: 13,
    color: theme.files.listItem.title,
  },
  focused: {
    fontWeight: 'bold',
  },
  selected: {
    color: theme.files.listItem.selectedText,
  },
  loading: {
    transform: [{scale: 0.75}],
    marginRight: 4,
    height: 24,
    width: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
